"use client";
import React from "react";
import FormProvider, { useFormik } from "providers/FormProvider";
import { Search } from "components/common/Formik";
import { assign, get, map } from "utils/lodash";
import { Select } from "components/common/Formik";
import { CATEGORIES } from "utils/constant";
import { useT } from "providers/I18nProvider";

import cx from "classnames";

// Sắp xếp phường/xã theo ABC tiếng Việt, bỏ tiền tố "Phường/Xã/Thị trấn" khi so
const stripPrefix = (s: string) => s.replace(/^(phường|xã|thị trấn|đặc khu)\s+/i, "");
// Nhóm: Phường trước, Xã sau, còn lại (Thị trấn/Đặc khu) cuối
const groupRank = (s: string) => (/^phường\s/i.test(s) ? 0 : /^xã\s/i.test(s) ? 1 : 2);
const sortByViName = (list: any[]) =>
  [...list].sort((a, b) => {
    const na = String(get(a, "name") ?? ""), nb = String(get(b, "name") ?? "");
    return groupRank(na) - groupRank(nb) ||
      stripPrefix(na).localeCompare(stripPrefix(nb), "vi", { numeric: true });
  });
// API đôi khi trả null/object thay vì mảng → .map crash cả trang /search
const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : []);

const Comp = ({
  onSearch,
  parentForm,
  onChangeProvince,
  onChangeWard,
  onClearProvince,
  onClearWard,
  onChangeCategory,
}: {
  onSearch: (searchTerm: string) => void;
  parentForm: any;
  onChangeProvince: (province: string | number) => void;
  onChangeWard: (ward: string | number) => void;
  onChangeCategory: (category: string | number) => void;
  onClearProvince: () => void;
  onClearWard: () => void;
}) => {
  const category = get(parentForm, "values.category");
  const [areaOpen, setAreaOpen] = React.useState(false);
  const provinceOptions = asArray(get(parentForm, "selectOptions.provinces"));
  const wardOptions = asArray(get(parentForm, "selectOptions.wards"));
  const categories = asArray(get(parentForm, "selectOptions.categories"));
  const t = useT();
  return (
    <div className="bg-white">
      <div className="flex flex-col container mx-auto p-4 md:p-6 gap-3 md:gap-4">
        <Search
          name="searchTerm"
          placeholder="Tìm cửa hàng"
          onSearch={onSearch}
          enterButton
        />
        {/* Mobile: khu vực thu gọn sau nút "Lọc khu vực" để danh sách lên sớm */}
        <button
          type="button"
          onClick={() => setAreaOpen((v) => !v)}
          aria-expanded={areaOpen}
          className="md:hidden inline-flex w-fit items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-[14px] text-gray-700"
        >
          {t("search.filterArea")}
          <span className={cx("transition-transform", { "rotate-180": areaOpen })}>▾</span>
        </button>
        <div className={cx("sm:flex-row flex-col gap-3 md:gap-4 md:flex", areaOpen ? "flex" : "hidden")}>
          <Select
            wrapperClassName="w-full overflow-hidden"
            name="province"
            placeholder="Tỉnh/Thành phố"
            options={provinceOptions.map((item: any) => ({
              label: get(item, "name"),
              value: get(item, "id"),
            }))}
            onChange={onChangeProvince}
            onClear={onClearProvince}
          />
          <Select
            wrapperClassName="w-full overflow-hidden"
            name="ward"
            placeholder="Phường/Xã"
            options={sortByViName(wardOptions).map((item: any) => ({
              label: get(item, "name"),
              value: get(item, "id"),
            }))}
            allowClear
            onChange={onChangeWard}
            onClear={onClearWard}
          />
        </div>
        {/* Chip danh mục: mobile cuộn ngang 1 hàng */}
        <div className="flex gap-2 md:gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
          {map(categories, (value, index) => {
            const cat: any = CATEGORIES.filter(
              (cat) => cat.code === value?.code
            )[0];
            if (!cat) return null;
            cat.id = value.id;
            return (
              <div
                onClick={() => {
                  if (get(cat, "code") === category) {
                    onChangeCategory("");
                  } else {
                    onChangeCategory(get(cat, "code"));
                  }
                }}
                key={`cat_${index}`}
                className={cx(
                  "shrink-0 cursor-pointer flex flex-col items-center justify-center bg-gray-100 px-3.5 py-2 rounded-full md:rounded-md md:p-2 border border-gray-200 text-gray-800 whitespace-nowrap",
                  {
                    ["bg-primary text-white"]: get(cat, "code") === category,
                  }
                )}
              >
                <span className="text-sm text-center ">
                  {get(cat, "nameKey") ? t(cat.nameKey) : get(cat, "name")}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Wrapper = ({
  onChangeSearch,
  onChangeFilter,
  duration = 800,
  parentForm,
}: {
  onChangeSearch: (searchTerm: string) => void;
  onChangeFilter: (filters: any) => void;
  duration?: number;
  value?: string;
  parentForm: any;
}) => {
  const ref: any = React.useRef(false);

  const form: any = useFormik({
    initialValues: {
      searchTerm: get(parentForm, "values.searchTerm"),
      province: get(parentForm, "values.province"),
      ward: get(parentForm, "values.ward"),
    },
    onSubmit: async (values) => {
      onChangeFilter(values);
    },
  });
  let timer: NodeJS.Timeout | undefined = undefined;
  React.useEffect(() => {
    if (ref.current) {
      if (!!timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!!onChangeSearch) {
          onChangeSearch(form.values.searchTerm);
        }
      }, duration);
    }
    return () => clearTimeout(timer);
  }, [form.values.searchTerm]);
  assign(form, {
    onSearch: (searchTerm: string) => {
      onChangeSearch(searchTerm);
    },
    onChangeProvince: (province: string) => {
      // Phường thuộc tỉnh cũ → reset khi đổi tỉnh.
      form.setFieldValue("ward", undefined);
      onChangeFilter({ province, ward: undefined });
    },
    onChangeWard: (ward: string) => {
      onChangeFilter({ ward });
    },
    onClearProvince: () => {
      form.setFieldValue("ward", undefined);
      onChangeFilter({ province: undefined, ward: undefined });
    },
    onClearWard: () => {
      onChangeFilter({ ward: undefined });
    },
    onChangeCategory: (category: string) => {
      onChangeFilter({ category });
    },
  });
  return (
    <FormProvider form={form}>
      <Comp
        onSearch={form.onSearch}
        parentForm={parentForm}
        onChangeProvince={form.onChangeProvince}
        onChangeWard={form.onChangeWard}
        onClearProvince={form.onClearProvince}
        onClearWard={form.onClearWard}
        onChangeCategory={form.onChangeCategory}
      />
    </FormProvider>
  );
};

export default Wrapper;
